import { Divider, HStack, Text } from "@chakra-ui/layout";
import {
  Card,
  CardBody,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Tooltip,
} from "@chakra-ui/react";
import { useCreditFacilityInterestExpenseSummaryRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useTranslation } from "react-i18next";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { formatPercentage } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const CreditFacilityExpenseEventSummaryCard: React.FC = () => {
  const { creditFacilityId, fundFamilyId, expenseEventId } =
    useRequiredStringParams();
  const { data: summaryData } =
    useCreditFacilityInterestExpenseSummaryRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      expenseEventId,
    );
  const { t } = useTranslation(["creditFacility"]);
  const showUpdatedRate =
    Number(summaryData.updated_average_interest_rate) > 0 &&
    formatPercentage(summaryData.updated_average_interest_rate ?? "0") !==
      formatPercentage(summaryData.average_interest_rate);
  const showAdjustmentNotVisibleTooltip =
    formatPercentage(summaryData.updated_average_interest_rate ?? "0") ==
      formatPercentage(summaryData.average_interest_rate) &&
    Number(summaryData.average_interest_rate) > 0;
  return (
    <Card variant="stat" w="100%" gap="0px" flexDirection="row">
      <CardBody>
        <CardSectionGroup flexWrap="wrap">
          <CardSection flexGrow="1">
            <CardSectionBody alignItems="stretch">
              <StatGroup alignItems="center">
                <Stat>
                  <StatLabel>
                    {t(
                      "creditFacility:postExpense.totalInterestExpenseForPeriod",
                    )}
                  </StatLabel>
                  <StatNumber width="360px">
                    {Number(summaryData.updated_total_expense) ? (
                      <HStack>
                        <Text
                          fontSize="20px"
                          fontWeight="400"
                          textDecoration="line-through"
                        >
                          {formatMoney(summaryData.total_expense)}
                        </Text>
                        <Text fontSize="20px" fontWeight="400">
                          →
                        </Text>
                        <Text fontSize="20px" fontWeight="400">
                          {formatMoney(summaryData.updated_total_expense)}
                        </Text>
                      </HStack>
                    ) : (
                      <Text fontSize="20px" fontWeight="400">
                        {formatMoney(summaryData.total_expense)}
                      </Text>
                    )}
                  </StatNumber>
                </Stat>
                <Divider orientation="vertical" />
                <Stat>
                  <StatLabel>
                    {t("creditFacility:postExpense.dailyAverageAllInRate")}
                  </StatLabel>
                  <StatNumber>
                    {showUpdatedRate ? (
                      <HStack>
                        <Text
                          fontSize="14px"
                          fontWeight="400"
                          textDecoration="line-through"
                        >
                          {formatPercentage(summaryData.average_interest_rate)}
                        </Text>
                        <Text fontSize="14px" fontWeight="400">
                          →
                        </Text>
                        <Text fontSize="14px" fontWeight="400">
                          {formatPercentage(
                            summaryData.updated_average_interest_rate ?? "0",
                          )}
                        </Text>
                      </HStack>
                    ) : showAdjustmentNotVisibleTooltip ? (
                      <Tooltip
                        label={t(
                          "creditFacility:postExpense.adjustmentNotVisible",
                        )}
                        placement="top"
                      >
                        <HStack alignItems="center">
                          <Text fontSize="14px" fontWeight="400">
                            {formatPercentage(
                              summaryData.average_interest_rate,
                            )}
                          </Text>
                          <InfoCircleIcon />
                        </HStack>
                      </Tooltip>
                    ) : (
                      <Text fontSize="14px" fontWeight="400">
                        {formatPercentage(summaryData.average_interest_rate)}
                      </Text>
                    )}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>
                    {t(
                      "creditFacility:postExpense.dailyAveragePrincipalOutstanding",
                    )}
                  </StatLabel>
                  <StatNumber>
                    <Text fontSize="14px" fontWeight="400">
                      {formatMoney(summaryData.average_principal_outstanding)}
                    </Text>
                  </StatNumber>
                </Stat>
              </StatGroup>
            </CardSectionBody>
          </CardSection>
        </CardSectionGroup>
      </CardBody>
    </Card>
  );
};
